import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Package, CheckCircle2, Truck, Home, Search } from "lucide-react";
import api, { inr, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const STEPS = [
  { key: "confirmed", label: "Confirmed", icon: CheckCircle2 },
  { key: "packed", label: "Packed", icon: Package },
  { key: "out-for-delivery", label: "Out for Delivery", icon: Truck },
  { key: "delivered", label: "Delivered", icon: Home },
];
const ORDER = ["confirmed", "packed", "out-for-delivery", "delivered"];

export default function TrackOrder() {
  const [params] = useSearchParams();
  const [num, setNum] = useState(params.get("order") || "");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const track = async (value) => {
    const on = (value ?? num).trim();
    if (!on) return;
    setLoading(true); setError(""); setOrder(null);
    try {
      const { data } = await api.get(`/orders/track/${on}`);
      setOrder(data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (params.get("order")) track(params.get("order")); /* eslint-disable-next-line */ }, []);

  const currentStep = order ? Math.max(0, ORDER.indexOf(order.status)) : 0;

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-8 py-16 fade-in">
      <div className="text-center mb-8">
        <span className="text-xs uppercase tracking-[0.3em] text-gold">Order Status</span>
        <h1 className="font-serif text-5xl text-forest mt-1">Track Your Order</h1>
        <p className="text-muted-foreground mt-2">Enter your order number to see live status.</p>
      </div>

      <div className="flex gap-2 max-w-md mx-auto">
        <Input value={num} onChange={(e) => setNum(e.target.value.toUpperCase())} placeholder="e.g. ADV2601010001" data-testid="track-input" className="rounded-full" />
        <Button onClick={() => track()} disabled={loading} data-testid="track-button" className="bg-gold text-forest hover:bg-gold-soft rounded-full px-6"><Search size={16} className="mr-1" /> Track</Button>
      </div>
      {error && <p className="text-center text-destructive text-sm mt-4" data-testid="track-error">{error}</p>}

      {order && (
        <div className="mt-10" data-testid="track-result">
          <div className="bg-white border border-border rounded-2xl p-6">
            <div className="flex justify-between items-center mb-8">
              <div><div className="text-xs text-muted-foreground uppercase tracking-wider">Order</div><div className="font-serif text-xl text-forest">{order.order_number}</div></div>
              <div className="text-right"><div className="text-xs text-muted-foreground uppercase tracking-wider">Total</div><div className="font-serif text-xl text-forest">{inr(order.total)}</div></div>
            </div>

            <div className="flex justify-between relative">
              <div className="absolute top-5 left-0 right-0 h-0.5 bg-border" />
              <div className="absolute top-5 left-0 h-0.5 bg-gold transition-all" style={{ width: `${(currentStep / (STEPS.length - 1)) * 100}%` }} />
              {STEPS.map((s, i) => (
                <div key={s.key} className="relative z-10 flex flex-col items-center gap-2 w-1/4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${i <= currentStep ? "bg-gold text-forest" : "bg-sand text-muted-foreground"}`}><s.icon size={18} /></div>
                  <span className={`text-xs text-center ${i <= currentStep ? "text-forest" : "text-muted-foreground"}`}>{s.label}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 border-t border-border pt-6 grid sm:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Items</div>
                {order.items.map((i, idx) => <div key={idx} className="text-forest">{i.name} ({i.variant}) × {i.qty}</div>)}
              </div>
              <div className="sm:text-right">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Delivering To</div>
                <div className="text-forest">{order.customer.name}</div>
                <div className="text-muted-foreground">{order.customer.address}, {order.customer.pincode}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
